import { Injectable } from '@angular/core';
import { ActionPerformed, PushNotificationSchema } from '@capacitor/push-notifications';
import { Observable, Subject } from 'rxjs';

/**
 * Event bus decoupling the low-level Capacitor push handler from consumers that need to react to
 * incoming push notifications (toast display, in-app notification list refresh, etc.).
 *
 * `PushNotificationService` publishes; higher-level state stores (which live in the UI layer and
 * therefore cannot be injected downwards into `services`) subscribe. This preserves the one-way
 * `ui -> services` dependency direction while still letting the store react to push events.
 */
@Injectable({ providedIn: 'root' })
export class PushNotificationEventsService {
    private readonly receivedSubject = new Subject<PushNotificationSchema>();
    private readonly actionPerformedSubject = new Subject<ActionPerformed>();

    /**
     * Emits when a push notification is delivered while the app is in the foreground.
     */
    readonly received$: Observable<PushNotificationSchema> = this.receivedSubject.asObservable();

    /**
     * Emits when the user taps a push notification from the OS tray (background or cold start).
     */
    readonly actionPerformed$: Observable<ActionPerformed> = this.actionPerformedSubject.asObservable();

    emitReceived(notification: PushNotificationSchema): void {
        this.receivedSubject.next(notification);
    }

    emitActionPerformed(action: ActionPerformed): void {
        this.actionPerformedSubject.next(action);
    }
}
